import { PlusOutlined } from '@ant-design/icons';
import { Button, Card, Input, Modal, Select, Table, TablePaginationConfig } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { notifyError, showConfirm } from '../../../store/utils';
import { PagedResultState } from '../../../common/CommonResult';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import { API } from '../../../api';
import { Editor } from '@tinymce/tinymce-react';

const PageList = () => {
  const [state, setState] = useState<PagedResultState<any>>({
    loading: true,
    result: {},
  });
  const [edit, setEdit] = useState<any>({ visible: false });

  const location = useLocation();
  const navigate = useNavigate();

  const editPage = useCallback(async (val: any) => {
    setEdit({
      visible: true,
      data: val,
    });
  }, []);

  const fetchData = useCallback(async () => {
    setState({ ...state, loading: true });
    try {
      const response = await API.get({ apiVersion: 'api' })(`/settings/pages${location.search}`);
      setState({
        loading: false,
        result: response.result,
      });
    } catch (error: any) {
      notifyError(error.message);
      setState({ ...state, loading: true, error: true });
    }
  }, [state, location]);

  const confirm = useCallback((value: any) => {
    showConfirm({
      title: 'Устгахдаа итгэлтэй байна уу?',
      content: 'Уг үйлдэл нь буцаагдахгүй!. Хэрэглэгч устахыг анхаарна уу',
      onOk() {
        pageDelete(value.no);
      },
      okText: 'Тийм',
      cancelText: 'Үгүй',
    });
  }, []);

  const pageDelete = useCallback(
    async (id: number) => {
      try {
        await API.delete({ apiVersion: 'api' })(`/settings/pages/${id}`);
        fetchData();
      } catch (error: any) {
        notifyError(error.message);
      }
    },
    [location]
  );

  const handleTableChange = useCallback(
    (pagination: TablePaginationConfig) => {
      const params = queryString.parse(location.search);
      const search = {
        ...params,
        page: pagination.current || 1,
        size: pagination.pageSize || 10,
      };
      navigate(`/settings/page?${queryString.stringify(search)}`);
    },
    [location]
  );

  useEffect(() => {
    if (!location.search) {
      navigate(`/settings/page?page=1&size=10`);
    }
  }, [location]);

  useEffect(() => {
    if (location.search) {
      fetchData();
    }
  }, [location.search]);

  const columns = useMemo(() => {
    return [
      {
        title: 'No',
        dataIndex: 'no',
      },
      {
        title: 'Нэр',
        dataIndex: 'title',
      },
      {
        title: 'Төрөл',
        dataIndex: 'type',
        render: (val: any) => <span>{(val || {}).text || ''}</span>,
      },
      {
        title: 'Бүртгэсэн огноо',
        dataIndex: 'createdAt',
        render: (val: string) => <span>{dayjs(val).format('YYYY-MM-DD')}</span>,
      },
      {
        title: 'Үйлдэл',
        render: (val: any) => (
          <div>
            <Button size="small" style={{ marginRight: 15 }} onClick={() => editPage(val)}>
              Засах
            </Button>
            <Button size="small" danger onClick={() => confirm(val)}>
              Устгах
            </Button>
          </div>
        ),
      },
    ];
  }, []);

  return (
    <Card
      title={`Хуудасны жагсаалт`}
      extra={
        <Button type="primary" onClick={() => setEdit({ visible: true })} icon={<PlusOutlined />}>
          Хуудас нэмэх
        </Button>
      }
    >
      <Table
        size="small"
        bordered
        loading={state.loading}
        dataSource={state.result?.data || []}
        rowKey={'id'}
        pagination={{
          current: state?.result?.page || 1,
          size: 'small',
          pageSize: state?.result?.size || 10,
          total: state?.result?.total,
        }}
        onChange={handleTableChange}
        columns={columns}
      />
      <UpdatePage
        data={edit?.data}
        visible={edit.visible}
        onClose={() => setEdit({ visible: false })}
        onReload={() => fetchData()}
      />
    </Card>
  );
};

type UpdatePageProps = {
  data?: any;
  visible: boolean;
  onClose: () => void;
  onReload: () => void;
};

const UpdatePage = ({ visible, data, onClose, onReload }: UpdatePageProps) => {
  const [loading, setLoading] = useState(false);
  const [state, updateState] = useState({
    title: '',
    type: 'SERVICE',
    content: '',
  });

  useEffect(() => {
    if (data?.no) {
      updateState({
        title: data.title,
        type: 'SERVICE',
        content: data.content,
      });
    }
  }, [data]);

  const close = useCallback(() => {
    updateState({
      title: '',
      type: 'SERVICE',
      content: '',
    });
    onClose();
  }, []);

  const onFinish = useCallback(async () => {
    try {
      setLoading(true);
      let payload = {
        no: data?.no,
        title: state.title,
        type: state.type,
        content: state.content,
      };
      await API.post({ apiVersion: 'api' })(`/settings/pages/update`, payload);
      close();
      onReload();
    } catch (error: any) {
      notifyError(error.message);
    } finally {
      setLoading(false);
    }
  }, [data, state]);

  return (
    <Modal
      title={`${data?.no ? 'Хуудас засах' : 'Хуудас нэмэх'}`}
      open={visible}
      width={800}
      footer={[
        <Button key="back" onClick={() => close}>
          Болих
        </Button>,
        <Button key="submit" loading={loading} type="primary" onClick={onFinish}>
          Хадгалах
        </Button>,
      ]}
      onCancel={() => close()}
    >
      <div style={{ marginBottom: 16 }}>
        <Input
          placeholder="Гарчиг оруулна уу"
          value={state.title}
          onChange={(e) => updateState({ ...state, title: e.target.value })}
        />
      </div>
      <div style={{ marginBottom: 16 }}>
        <Select
          style={{ width: '100%' }}
          value={state.type}
          onChange={(e) => updateState({ ...state, type: e })}
        >
          <Select.Option value="SERVICE">Хүргэлтийн үйлчилгээний</Select.Option>
        </Select>
      </div>
      <div style={{ marginBottom: 16 }}>
        <Editor
          apiKey="la2xtt7byyipi7bn4kzwe9csy0lt80pbhc2iorx4l4l56pvd"
          value={state.content || ''}
          init={{
            plugins: ['lists link textcolor'],
            height: 300,
            menubar: false,
            toolbar:
              'bold italic backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat',
          }}
          onEditorChange={(value: string) => updateState({ ...state, content: value })}
        />
      </div>
    </Modal>
  );
};

export default PageList;
